/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\LogPermissionButtonGroup.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Log Profiles module.
----------------------------------------------------------------*/


import React from 'react';
import { Bell, FileText, Plus, Edit, Trash2 } from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';

/** Log-permission key names supported by the log profile system */
export type LogPermKey = 'logCreate' | 'logEdit' | 'logDelete';
/** Notify-permission key names supported by the log profile system */
export type NotifyPermKey = 'notifyCreate' | 'notifyEdit' | 'notifyDelete';
/** Union of all permission key names */
export type PermKey = LogPermKey | NotifyPermKey;

interface ButtonDef {
  key: PermKey;
  labelKey: string;
  icon: React.ElementType;
  color: 'green' | 'blue' | 'red';
}

const LOG_TYPES: ButtonDef[] = [
  { key: 'logCreate',    labelKey: 'logProfiles.forms.labels.logCreate',    icon: Plus,   color: 'green' },
  { key: 'logEdit',      labelKey: 'logProfiles.forms.labels.logEdit',      icon: Edit,   color: 'blue'  },
  { key: 'logDelete',    labelKey: 'logProfiles.forms.labels.logDelete',    icon: Trash2, color: 'red'   },
];

const NOTIFY_TYPES: ButtonDef[] = [
  { key: 'notifyCreate', labelKey: 'logProfiles.forms.labels.notifyCreate', icon: Plus,   color: 'green' },
  { key: 'notifyEdit',   labelKey: 'logProfiles.forms.labels.notifyEdit',   icon: Edit,   color: 'blue'  },
  { key: 'notifyDelete', labelKey: 'logProfiles.forms.labels.notifyDelete', icon: Trash2, color: 'red'   },
];

interface LogPermissionButtonGroupProps {
  /** Numeric ID of the menu node whose permissions are being rendered */
  nodeId: number;
  /**
   * Returns the current active state for a given permission key.
   * For leaf nodes use `getPermissionValue`; for parent nodes use
   *`allChildrenHavePermission`.
   */
  getIsActive: (key: PermKey) => boolean;
  /** Returns whether the permission is inherited from a parent */
  getIsInherited: (key: PermKey) => boolean;
  /** Called when the user clicks a permission button to toggle it */
  onToggle: (key: PermKey, newValue: boolean) => void;
}

/**
 * LogPermissionButtonGroup
 *
 * Pair of horizontal button strips showing log/notify permission toggles
 * for one node in the hierarchy.  Each strip has three pills:
 * Create (green) � Edit (blue) � Delete (red).
 */
export function LogPermissionButtonGroup({
  nodeId: _nodeId, // only held for future aria-label references
  getIsActive,
  getIsInherited,
  onToggle,
}: LogPermissionButtonGroupProps) {
  const { t } = useI18n();

  const renderStrip = (types: ButtonDef[], stripLabel: string) => (
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-2">
        {stripLabel === 'log'
          ? <FileText className="w-4 h-4 text-muted-foreground" />
          : <Bell className="w-4 h-4 text-muted-foreground" />}
        <span className="text-sm font-medium text-foreground">
          {t(`logProfiles.forms.${stripLabel}s`)}
        </span>
      </div>
      <div className="flex gap-1">
        {types.map(({ key, labelKey, icon: Icon, color }) => {
          const label = t(labelKey);
          const isActive = getIsActive(key);
          const isInherited = getIsInherited(key);
          const colorClasses = {
            green: 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900/20',
            blue: 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/20',
            red: 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900/20',
          };

          return (
            <button
              key={key}
              type="button"
              onClick={() => onToggle(key, !isActive)}
              className={`px-2 py-1 text-xs font-medium rounded-full transition-colors flex items-center gap-1 relative ${
                isActive
                  ? colorClasses[color]
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent'
              }`}
              aria-label={`${stripLabel} ${label}`}
            >
              <Icon className="w-3 h-3" />
              {label}
              {isInherited && (
                <div className="absolute -top-1 -right-1 w-1.5 h-1.5 bg-blue-500 rounded-full border border-white dark:border-gray-800" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="flex gap-6">
      {renderStrip(LOG_TYPES,    'log')}
      
    </div>
  );
}
